import { LegalShell, type LegalSection } from './_shared';

const SECTIONS: LegalSection[] = [
  {
    h: 'Who can use Nearmirer',
    body: (
      <>
        <p>
          You must be at least 18 years old and legally able to form a binding contract in your
          country of residence. Nearmirer is not directed to and may not be used by anyone under 18.
          If we learn that an account belongs to someone under 18, we will terminate it.
        </p>
        <p>
          You may not use Nearmirer if you are barred from doing so under applicable law, if your
          account was previously terminated for a policy violation, or if you are a registered sex
          offender.
        </p>
      </>
    ),
  },
  {
    h: 'The account',
    body: (
      <p>
        You sign in with an email address and a one-time code. You are responsible for keeping
        access to that inbox secure and for all activity under your account. Tell us promptly at
        security@nearmirer.com if you suspect unauthorized use. We keep a record of your sign-ins
        (time and IP address) for security and abuse prevention.
      </p>
    ),
  },
  {
    h: 'How Nearmirer works — and what it is not',
    body: (
      <>
        <p>
          Nearmirer lets people who are physically near each other and who have opted in ("Glow On")
          discover one another and exchange anonymous messages ("Sparks"), anonymous 1:1 threads
          ("Whispers") and location-scoped group chat ("Circles").
        </p>
        <p>
          Nearmirer is an introduction and messaging service. It is not a background-check service,
          it does not verify the identity, age, character, or intentions of any user, and it does
          not supervise interactions or meetings that happen off the platform. Any decision to meet,
          communicate with, or trust another user is yours alone and at your own risk.
        </p>
      </>
    ),
  },
  {
    h: 'Your content and conduct',
    body: (
      <>
        <p>
          You are solely responsible for the content you send. By sending content you grant
          Nearmirer a worldwide, non-exclusive, royalty-free license to host, store, transmit,
          display and moderate it for the purpose of operating and improving the service.
        </p>
        <p>
          You agree to follow our{' '}
          <a href="/community-guidelines" className="underline">
            Community Guidelines
          </a>
          , which are part of these Terms. In short: no harassment, hate, threats, sexual content
          directed at people who did not ask for it, sexual content involving minors, doxxing, spam,
          impersonation, or illegal activity. We may remove content and suspend or terminate
          accounts that violate these Terms, with or without notice.
        </p>
      </>
    ),
  },
  {
    h: 'Location data',
    body: (
      <p>
        Nearmirer needs your device location to work. We collect and use precise location only while
        you are actively using the app and only to place you at a venue and to power the nearby
        radar. We do not build or keep a history of where you have been. Full detail is in our{' '}
        <a href="/privacy" className="underline">
          Privacy Policy
        </a>
        . You can turn location off in your device settings at any time; most features will stop
        working.
      </p>
    ),
  },
  {
    h: 'Safety',
    body: (
      <>
        <p>
          Never share your home address, financial information, or government identifiers. If you
          choose to meet someone, meet in a public place, tell a friend, and arrange your own
          transport. Report anyone who makes you uncomfortable — reports go to a human moderation
          team.
        </p>
        <p>
          Nearmirer is not responsible for the conduct of any user, on or off the platform. To the
          fullest extent permitted by law, you release Nearmirer from claims arising out of your
          interactions with other users.
        </p>
      </>
    ),
  },
  {
    h: 'Moderation and enforcement',
    body: (
      <>
        <p>
          We may review content that is reported to us or automatically flagged, and we may act on
          it — including removing content, limiting or throttling features, muting, suspending, or
          permanently banning an account, and ending your active sessions. We aim to be fair and
          consistent but we do not guarantee that every report leads to the same outcome. Serious
          safety issues may be reported to law enforcement.
        </p>
        <p>
          We keep an internal record of enforcement decisions and any notes our moderators add to an
          account. Repeat or severe violations escalate. If you are sanctioned you may{' '}
          <strong>appeal</strong> from the app or by emailing safety@nearmirer.com; a different
          reviewer considers each appeal. What a moderator can and cannot see about your account is
          set out in the{' '}
          <a href="/privacy" className="underline">
            Privacy Policy
          </a>
          .
        </p>
      </>
    ),
  },
  {
    h: 'Intellectual property',
    body: (
      <p>
        Nearmirer and its logos, design, and software are owned by Nearmirer, Inc. and protected by
        intellectual-property laws. We grant you a limited, revocable, non-transferable license to
        use the app for personal, non-commercial purposes. Copyright complaints may be sent to
        dmca@nearmirer.com under the U.S. Digital Millennium Copyright Act.
      </p>
    ),
  },
  {
    h: 'Disclaimers',
    body: (
      <p className="uppercase">
        The service is provided "as is" and "as available" without warranties of any kind, express
        or implied, including merchantability, fitness for a particular purpose, and
        non-infringement. Nearmirer does not warrant that the service will be uninterrupted, secure,
        or error-free, or that any user is who they claim to be.
      </p>
    ),
  },
  {
    h: 'Limitation of liability',
    body: (
      <p className="uppercase">
        To the fullest extent permitted by law, Nearmirer, Inc. and its officers, employees and
        agents will not be liable for any indirect, incidental, special, consequential or punitive
        damages, or any loss of data, goodwill, or other intangible losses, arising from your use of
        the service or your interactions with other users. Our total liability for any claim
        relating to the service will not exceed the greater of USD 100 or the amount you paid us in
        the 12 months before the claim.
      </p>
    ),
  },
  {
    h: 'Indemnification',
    body: (
      <p>
        You agree to indemnify and hold Nearmirer harmless from any claim or demand, including
        reasonable legal fees, arising out of your content, your use of the service, or your
        violation of these Terms or the rights of another person.
      </p>
    ),
  },
  {
    h: 'Governing law and dispute resolution',
    body: (
      <>
        <p>
          These Terms are governed by the laws of the State of Delaware, USA, without regard to
          conflict-of-laws rules. Except where prohibited by your local law, any dispute will be
          resolved by binding individual arbitration rather than in court, and you and Nearmirer
          waive the right to a jury trial and to participate in a class action. You may opt out of
          arbitration within 30 days of first accepting these Terms by emailing legal@nearmirer.com.
        </p>
        <p>
          If you are a consumer in the EU, UK, or another jurisdiction whose law gives you
          non-waivable rights (including the right to bring claims locally), nothing here removes
          those rights.
        </p>
      </>
    ),
  },
  {
    h: 'Changes and termination',
    body: (
      <p>
        We may update these Terms. If a change is material we will notify you in the app and ask you
        to accept the new version before continuing. You may stop using Nearmirer and delete your
        account at any time from Settings. We may suspend or end your access if you violate these
        Terms or if we discontinue the service.
      </p>
    ),
  },
  {
    h: 'Contact',
    body: <p>Nearmirer, Inc. — legal@nearmirer.com.</p>,
  },
];

export function TermsPage() {
  return (
    <LegalShell
      title="Terms of Service"
      path="/terms"
      seoDescription="The Terms of Service for Nearmirer, the near-me app for meeting nearby people and sending anonymous compliments. 18+, worldwide."
      intro={
        <p>
          These Terms are a contract between you and Nearmirer, Inc. By creating an account or using
          Nearmirer you agree to them. Please read the disclaimers, the limitation of liability, and
          the arbitration section carefully — they affect your legal rights.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
